/**
 * Enriquecimento local de places.
 *
 * A extensão continua executando o fetch/extrator cross-origin pelo get.html; este módulo é a
 * parte que o painel orquestra: monta o plano a partir do IndexedDB, agrupa resultados por CID,
 * faz o merge aditivo e persiste. Nenhum endpoint de seleção/salvamento do legado participa.
 */
import {
  getLists,
  getPage,
  patchList,
  updateLocalPlacesByTarget,
  type LocalPlace,
  type LocalPlaceMapsContacts,
  type LocalPlaceTarget,
} from '../data/localDb';
import type { PlacePhone, PlaceSocialNetwork } from '../types/place';

export const LOCAL_ENHANCE_PREFIX = 'local:';
export const ENHANCE_FRESHNESS_MS = 30 * 24 * 60 * 60 * 1_000;

/** Porta literal da blacklist usada por `/app/list/places` [app.js:37-51]. */
export const ENHANCE_BLACKLIST = [
  'x.com',
  'twitter.com',
  'facebook.com',
  'instagram.com',
  'youtube.com',
  'tiktok.com',
  'linkedin.com',
  'pinterest.com',
  'medium.com',
  'wa.me',
  'google.com',
  'api.whatsapp.com',
  'wa.link',
] as const;

export interface EnhancePlaceSite {
  /** Identidade opaca para o get.html; o worker não consulta MongoDB. */
  _id: string;
  cid: string;
  websites: string[];
  /** Cópias locais do mesmo negócio que recebem o merge coletivo por CID. */
  targets: LocalPlaceTarget[];
  /** Nome do negócio, para a janela mostrar o que cada um ganhou. */
  title?: string;
}

export interface EnhanceTotals {
  emails: number;
  phones: number;
  social_networks: number;
  /** CNPJ novo (o existente nunca é trocado). */
  cnpj?: number;
}

/** O que um negócio ganhou numa gravação. */
export interface EnhancePlaceGain {
  emails: number;
  phones: number;
  /** Nomes das redes novas ("Instagram"). */
  social_networks: string[];
  cnpj: boolean;
}

/**
 * Negócio com site enriquecido há menos de 30 dias, nesta seleção ou em outra lista: não precisa
 * de visita. Sem nenhum site para visitar, a janela mostra a rodada sem acessar os sites e marca
 * as listas (decisão 176).
 */
export interface EnhanceKnownPlace {
  _id: string;
  cid: string;
  title?: string;
  /** Sites próprios, para a rodada mostrar por onde passa. */
  websites: string[];
  /** Cópias do negócio nas listas escolhidas. */
  targets: LocalPlaceTarget[];
  /** O que o enriquecimento trouxe para as cópias escolhidas. */
  gain: EnhancePlaceGain;
  /**
   * Cópias escolhidas que ainda não têm o enriquecimento mais recente (feito em outra lista):
   * recebem os dados daquela cópia no fim da rodada, mantendo a data de origem (decisão 174).
   */
  reuse?: {
    targets: LocalPlaceTarget[];
    data: EnhancedPlaceData;
    enhancedAt: string;
  };
}

/** Plano da janela: quem entra, quem já foi enriquecido e quem fica de fora. */
export interface EnhancePlan {
  sites: EnhancePlaceSite[];
  /** Negócios das listas escolhidas (um por CID). */
  candidates: number;
  /** Enriquecidos há menos de 30 dias, nesta seleção ou em outra lista. */
  known: EnhanceKnownPlace[];
  /** Todas as listas escolhidas já estão marcadas como enriquecidas. */
  enriched: boolean;
  skipped: {
    /** Sem site próprio: só rede social, WhatsApp ou nada. */
    noSite: number;
    /** Enriquecidos há menos de 30 dias (os mesmos de `known`). */
    recent: number;
  };
  /** Quando o primeiro dos recentes volta a poder ser enriquecido (ISO). */
  nextAvailableAt?: string;
}

export interface EnhancedPlaceData {
  emails?: string[];
  websites?: string[];
  phones?: PlacePhone[];
  social_networks?: PlaceSocialNetwork[] | Record<string, string>;
  cnpj?: string;
}

export interface EnhanceQueueItem extends EnhancedPlaceData {
  id?: string;
  cid?: string;
  url?: string;
  links?: Record<string, string>;
  error?: unknown;
  [key: string]: unknown;
}

export interface SaveLocalEnhancedPlacesOptions {
  userId: string;
  sites: readonly EnhancePlaceSite[];
  /** Já enriquecidos: os que vieram de outra lista recebem os dados de lá. */
  known?: readonly EnhanceKnownPlace[];
  lists?: readonly string[];
  finished?: boolean;
  now?: Date;
}

/**
 * Compara pelo domínio, como `mainSite` dos cartões: o `url.includes` do legado tratava
 * `odontomix.com.br` como `x.com` e tirava o negócio do enriquecimento.
 */
export function isBlacklisted(url: string): boolean {
  const host =
    url
      .trim()
      .replace(/^[a-z][a-z\d+.-]*:\/\//i, '')
      .replace(/^www\./i, '')
      .split(/[/?#:]/)[0]
      ?.toLowerCase() ?? '';
  return ENHANCE_BLACKLIST.some((domain) => host === domain || host.endsWith(`.${domain}`));
}

function newestTimestamp(left: number | undefined, value: string | undefined): number | undefined {
  if (!value) return left;
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return left;
  return left === undefined ? parsed : Math.max(left, parsed);
}

/**
 * Porta de `/app/list/places` [app.js:2959-3028]: janela de 30 dias, blacklist e um negócio por
 * CID. A diferença de fonte é deliberada pela D7: os websites já estão no LocalPlace.
 */
export async function getLocalPlacesToEnhance(
  listIds: readonly string[],
  userId: string,
  now = Date.now(),
): Promise<EnhancePlaceSite[]> {
  return (await planLocalEnhance(listIds, userId, now)).sites;
}

function emptyGain(): EnhancePlaceGain {
  return { emails: 0, phones: 0, social_networks: [], cnpj: false };
}

/** O que uma gravação acrescentou a uma cópia. */
function copyGain(before: LocalPlace, after: LocalPlace): EnhancePlaceGain {
  const known = new Set((before.social_networks ?? []).map((network) => network.name));
  return {
    emails: after.enhancedCount?.emails ?? 0,
    phones: after.enhancedCount?.phones ?? 0,
    social_networks: (after.social_networks ?? [])
      .map((network) => network.name)
      .filter((name) => name && !known.has(name)),
    cnpj: !before.cnpj && Boolean(after.cnpj),
  };
}

/**
 * O que o enriquecimento já gravado trouxe: `enhancedCount`, com as redes novas no fim da lista
 * (o merge acrescenta depois das existentes). CNPJ só conta nas gravações que o registram.
 */
function storedGain(copy: LocalPlace): EnhancePlaceGain {
  const count = copy.enhancedCount;
  const networks = count?.social_networks ?? 0;
  return {
    emails: count?.emails ?? 0,
    phones: count?.phones ?? 0,
    social_networks:
      networks > 0 ? (copy.social_networks ?? []).slice(-networks).map((network) => network.name) : [],
    cnpj: (count?.cnpj ?? 0) > 0,
  };
}

/** O mesmo negócio pode estar em várias listas: conta a cópia que mais ganhou. */
function largerGain(left: EnhancePlaceGain, right: EnhancePlaceGain): EnhancePlaceGain {
  return {
    emails: Math.max(left.emails, right.emails),
    phones: Math.max(left.phones, right.phones),
    social_networks:
      right.social_networks.length > left.social_networks.length
        ? right.social_networks
        : left.social_networks,
    cnpj: left.cnpj || right.cnpj,
  };
}

function knownPlace(
  cid: string,
  websites: ReadonlySet<string>,
  copies: readonly LocalPlace[],
  source: LocalPlace | undefined,
  title: string | undefined,
): EnhanceKnownPlace {
  const sourceAt = source?.enhancedAt ? Date.parse(source.enhancedAt) : Number.NaN;
  // Cópias escolhidas sem o enriquecimento mais recente (feito em outra lista) recebem os dados
  // daquela cópia; as outras já têm o que ele trouxe.
  const stale = source?.enhancedAt
    ? copies.filter((copy) => !(Date.parse(copy.enhancedAt ?? '') >= sourceAt))
    : [];
  const data: EnhancedPlaceData = {
    emails: source?.emails,
    websites: source?.websites,
    phones: source?.phones,
    social_networks: source?.social_networks,
    cnpj: source?.cnpj,
  };
  let gain = emptyGain();
  for (const copy of copies) {
    gain = largerGain(
      gain,
      stale.includes(copy) && source?.enhancedAt
        ? copyGain(copy, mergeEnhancedPlace(copy, data, source.enhancedAt))
        : storedGain(copy),
    );
  }
  const target = (copy: LocalPlace): LocalPlaceTarget => ({
    listId: copy.listId,
    key: copy.key,
    cid: copy.cid,
  });
  return {
    _id: `${LOCAL_ENHANCE_PREFIX}${cid}`,
    cid,
    ...(title ? { title } : {}),
    websites: [...websites],
    targets: copies.map(target),
    gain,
    ...(stale.length > 0 && source?.enhancedAt
      ? { reuse: { targets: stale.map(target), data, enhancedAt: source.enhancedAt } }
      : {}),
  };
}

/**
 * O mesmo plano, com os negócios que ficam de fora contados por motivo (proposta "Enriching").
 * `onlyCids` limita aos negócios escolhidos ("Enriquecer os N novos" dos semelhantes, 3B).
 */
export async function planLocalEnhance(
  listIds: readonly string[],
  userId: string,
  now = Date.now(),
  onlyCids?: readonly string[],
): Promise<EnhancePlan> {
  const empty: EnhancePlan = {
    sites: [],
    candidates: 0,
    known: [],
    enriched: false,
    skipped: { noSite: 0, recent: 0 },
  };
  if (!userId || listIds.length === 0) return empty;

  const selected = new Set(listIds);
  const only = onlyCids ? new Set(onlyCids) : undefined;
  const lists = (await getLists(userId)).filter((list) => list.module === 'places');
  const snapshots = await Promise.all(
    lists.map(async (list) => ({
      list,
      // Tirados da lista (decisão 199) não são enriquecidos.
      records: (await getPage<LocalPlace>('places', list.id)).records.filter(
        (record) => !record.removedAt,
      ),
    })),
  );
  const byCid = new Map<
    string,
    {
      selected: boolean;
      websites: Set<string>;
      targets: Map<string, LocalPlaceTarget>;
      /** Cópias nas listas escolhidas. */
      selectedCopies: LocalPlace[];
      newestEnhancedAt?: number;
      /** A cópia enriquecida mais recente, de qualquer lista. */
      newestEnhanced?: LocalPlace;
      title?: string;
    }
  >();
  const candidateOrder: string[] = [];
  const candidates = new Set<string>();

  for (const { list, records } of snapshots) {
    for (const place of records) {
      if (!place.cid) continue;
      const row = byCid.get(place.cid) ?? {
        selected: false,
        websites: new Set<string>(),
        targets: new Map<string, LocalPlaceTarget>(),
        selectedCopies: [],
      };
      const target = { listId: place.listId, key: place.key, cid: place.cid };
      if (selected.has(list.id) && (!only || only.has(place.cid))) {
        row.selected = true;
        row.selectedCopies.push(place);
        if (!candidates.has(place.cid)) {
          candidates.add(place.cid);
          candidateOrder.push(place.cid);
        }
      }
      for (const website of place.websites ?? []) {
        if (website && !isBlacklisted(website)) row.websites.add(website);
      }
      row.targets.set(`${place.listId}\u0000${place.key}`, target);
      const newest = newestTimestamp(row.newestEnhancedAt, place.enhancedAt);
      if (newest !== row.newestEnhancedAt) row.newestEnhanced = place;
      row.newestEnhancedAt = newest;
      row.title ??= place.title;
      byCid.set(place.cid, row);
    }
  }

  const freshnessFloor = now - ENHANCE_FRESHNESS_MS;
  const skipped = { noSite: 0, recent: 0 };
  const known: EnhanceKnownPlace[] = [];
  let nextAvailable: number | undefined;
  const sites = candidateOrder.flatMap((cid) => {
    const row = byCid.get(cid);
    if (!row?.selected) return [];
    if (row.websites.size === 0) {
      skipped.noSite += 1;
      return [];
    }
    if (row.newestEnhancedAt !== undefined && row.newestEnhancedAt >= freshnessFloor) {
      known.push(knownPlace(cid, row.websites, row.selectedCopies, row.newestEnhanced, row.title));
      skipped.recent += 1;
      const availableAt = row.newestEnhancedAt + ENHANCE_FRESHNESS_MS;
      nextAvailable = nextAvailable === undefined ? availableAt : Math.min(nextAvailable, availableAt);
      return [];
    }
    return [{
      _id: `${LOCAL_ENHANCE_PREFIX}${cid}`,
      cid,
      websites: [...row.websites],
      targets: [...row.targets.values()],
      ...(row.title ? { title: row.title } : {}),
    }];
  });
  const chosen = lists.filter((list) => selected.has(list.id));
  return {
    sites,
    candidates: candidateOrder.length,
    known,
    enriched: chosen.length > 0 && chosen.every((list) => Boolean(list.enrichedAt)),
    skipped,
    ...(nextAvailable !== undefined ? { nextAvailableAt: new Date(nextAvailable).toISOString() } : {}),
  };
}

function mergeStrings(original: readonly string[] | undefined, update: readonly string[] | undefined): string[] {
  return [...new Set([...(original ?? []), ...(update ?? [])])];
}

function normalizeSocial(
  value: EnhancedPlaceData['social_networks'],
): PlaceSocialNetwork[] {
  if (Array.isArray(value)) {
    return value.filter(
      (item): item is PlaceSocialNetwork => Boolean(item?.name && item?.url),
    );
  }
  if (!value || typeof value !== 'object') return [];
  return Object.entries(value)
    .filter(([name, url]) => Boolean(name && url))
    .map(([name, url]) => ({ name, url }));
}

function mergeObjects<T extends object>(
  original: readonly T[] | undefined,
  update: readonly T[] | undefined,
  identity: keyof T,
): T[] {
  const merged = new Map<unknown, T>();
  for (const item of [...(original ?? []), ...(update ?? [])]) {
    const key = item?.[identity];
    if (key) merged.set(key, item);
  }
  return [...merged.values()];
}

function countAdded(before: readonly unknown[] | undefined, after: readonly unknown[]): number {
  return Math.max(0, after.length - (before?.length ?? 0));
}

function dropLast<T>(values: readonly T[] | undefined, count = 0): T[] | undefined {
  return values ? values.slice(0, Math.max(0, values.length - count)) : undefined;
}

/**
 * Contatos que vieram do Google Maps. Quem foi enriquecido antes de 24/10/2026 não tem a cópia
 * guardada: o merge acrescenta no fim, então sai do fim o que a última gravação trouxe
 * (`enhancedCount`). Erra só para quem foi enriquecido mais de uma vez.
 */
export function mapsContactsOf(place: LocalPlace): LocalPlaceMapsContacts {
  if (place.mapsContacts) return place.mapsContacts;
  const count = place.enhancedAt ? place.enhancedCount : undefined;
  const contacts: LocalPlaceMapsContacts = {
    phones: dropLast(place.phones, count?.phones),
    websites: dropLast(place.websites, count?.websites),
    social_networks: dropLast(place.social_networks, count?.social_networks),
  };
  return Object.fromEntries(
    Object.entries(contacts).filter(([, value]) => value !== undefined),
  ) as LocalPlaceMapsContacts;
}

/**
 * Lista sem a marca de enriquecida (decisão 177): só o que veio do Maps. Email e CNPJ nunca vêm
 * do Maps; telefones, sites e redes voltam ao que eram antes do enriquecimento.
 */
export function withoutEnrichment(place: LocalPlace): LocalPlace {
  const maps = mapsContactsOf(place);
  return {
    ...place,
    phones: maps.phones,
    websites: maps.websites,
    social_networks: maps.social_networks,
    emails: undefined,
    cnpj: undefined,
  };
}

/**
 * União aditiva portada de `mergeContact` [app.js:106-182]. Strings usam Set, redes usam
 * `name`, telefones usam `formated`, e a informação nova vence só na mesma identidade.
 */
export function mergeEnhancedPlace(
  original: LocalPlace,
  update: EnhancedPlaceData,
  enhancedAt: string,
): LocalPlace {
  const websites = mergeStrings(original.websites, update.websites);
  const emails = mergeStrings(original.emails, update.emails);
  const socialNetworks = mergeObjects(
    original.social_networks,
    normalizeSocial(update.social_networks),
    'name',
  );
  const phones = mergeObjects(original.phones, update.phones, 'formated');

  return {
    ...original,
    websites,
    emails,
    social_networks: socialNetworks,
    phones,
    // O cache compartilhado do S5.5 preserva o CNPJ existente; o merge local faz o mesmo.
    cnpj: original.cnpj || update.cnpj,
    mapsContacts: mapsContactsOf(original),
    enhancedAt,
    enhancedCount: {
      websites: countAdded(original.websites, websites),
      emails: countAdded(original.emails, emails),
      social_networks: countAdded(original.social_networks, socialNetworks),
      phones: countAdded(original.phones, phones),
      // Só quando entra um CNPJ novo, para a rodada sem visita saber o que veio (decisão 176).
      ...(!original.cnpj && update.cnpj ? { cnpj: 1 } : {}),
    },
  };
}

/** Consolida os vários sites do mesmo CID sem deixar o último sobrescrever os anteriores. */
export function aggregateEnhancedQueueItems(
  items: readonly EnhanceQueueItem[],
): Record<string, EnhancedPlaceData> {
  const result: Record<string, EnhancedPlaceData> = {};
  for (const item of items) {
    const id = String(item.id ?? '');
    if (!id) continue;
    const current = result[id] ?? {};
    const websites = item.error
      ? []
      : [...(item.websites ?? []), ...(item.url ? [item.url] : [])];
    result[id] = {
      emails: mergeStrings(current.emails, item.error ? [] : item.emails),
      websites: mergeStrings(current.websites, websites),
      phones: mergeObjects(current.phones, item.error ? [] : item.phones, 'formated'),
      social_networks: mergeObjects(
        normalizeSocial(current.social_networks),
        item.error ? [] : normalizeSocial(item.social_networks ?? item.links),
        'name',
      ),
      cnpj: current.cnpj || (item.error ? undefined : item.cnpj),
    };
  }
  return result;
}

/** IDs cujos sites todos já chegaram ao completed[] da fila serial da extensão. */
export function readyEnhanceIds(
  completed: readonly EnhanceQueueItem[],
  sites: readonly EnhancePlaceSite[],
  persisted: ReadonlySet<string>,
): string[] {
  const counts = new Map<string, number>();
  for (const item of completed) {
    const id = String(item.id ?? '');
    if (id) counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  return sites
    .filter((site) => !persisted.has(site._id) && (counts.get(site._id) ?? 0) >= site.websites.length)
    .map((site) => site._id);
}

/**
 * Persiste um conjunto de CIDs concluídos e, no fechamento, marca as listas enriquecidas. Os já
 * enriquecidos em outra lista (`known` com `reuse`) recebem os dados de lá na mesma gravação.
 */
export async function saveLocalEnhancedPlaces(
  places: Readonly<Record<string, EnhancedPlaceData>>,
  options: SaveLocalEnhancedPlacesOptions,
): Promise<{ total: EnhanceTotals; gains: Record<string, EnhancePlaceGain> }> {
  const enhancedAt = (options.now ?? new Date()).toISOString();
  const sitesById = new Map(options.sites.map((site) => [site._id, site]));
  const updatesByCid = new Map<string, EnhancedPlaceData>();
  const targets: LocalPlaceTarget[] = [];

  for (const [id, update] of Object.entries(places)) {
    const site = sitesById.get(id);
    if (!site) continue;
    updatesByCid.set(site.cid, update);
    targets.push(...site.targets);
  }

  const changed = await updateLocalPlacesByTarget(
    options.userId,
    targets,
    (current) => {
      const update = updatesByCid.get(current.cid);
      return update ? mergeEnhancedPlace(current, update, enhancedAt) : current;
    },
    enhancedAt,
  );

  const reused = (options.known ?? []).filter((row) => row.reuse);
  if (reused.length > 0) {
    const reuseByCid = new Map(reused.map((row) => [row.cid, row.reuse]));
    await updateLocalPlacesByTarget(
      options.userId,
      reused.flatMap((row) => row.reuse?.targets ?? []),
      (current) => {
        const reuse = reuseByCid.get(current.cid);
        return reuse ? mergeEnhancedPlace(current, reuse.data, reuse.enhancedAt) : current;
      },
      enhancedAt,
    );
  }

  const byCid = new Map<string, EnhancePlaceGain>();
  for (const { before, after } of changed) {
    byCid.set(after.cid, largerGain(byCid.get(after.cid) ?? emptyGain(), copyGain(before, after)));
  }
  const total = [...byCid.values()].reduce<EnhanceTotals>(
    (sum, row) => ({
      emails: sum.emails + row.emails,
      phones: sum.phones + row.phones,
      social_networks: sum.social_networks + row.social_networks.length,
      cnpj: (sum.cnpj ?? 0) + (row.cnpj ? 1 : 0),
    }),
    { emails: 0, phones: 0, social_networks: 0, cnpj: 0 },
  );
  const gains: Record<string, EnhancePlaceGain> = {};
  for (const id of Object.keys(places)) {
    const site = sitesById.get(id);
    if (site) gains[id] = byCid.get(site.cid) ?? emptyGain();
  }

  if (options.finished) await markListsEnriched(options.userId, options.lists ?? [], enhancedAt);

  return { total, gains };
}

async function markListsEnriched(
  userId: string,
  listIds: readonly string[],
  enrichedAt: string,
): Promise<void> {
  const owned = new Map((await getLists(userId)).map((list) => [list.id, list]));
  await Promise.all(
    [...new Set(listIds)].map(async (listId) => {
      const list = owned.get(listId);
      if (list?.module === 'places') await patchList(listId, { enrichedAt });
    }),
  );
}
